import BatFlapping from "components/BatFlapping";
import Button, { ButtonContainer } from "components/Button/v2";
import GameSettings from "components/GameSettings";
import VIPExitButton from "components/VIPExitButton";
import RoleConfirmationModal from "components/RoleConfirmationModal";
import VampirePartyTitle, { Wrapper } from "containers/Game/VampirePartyTitle";
import { processText } from "core/utils/string";
import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { web, desktop } from "submodules/sharedText/flavor";
import QRCode from "qrcode.react";
import { SelectableLink } from "components/Link";
import { TextStyle } from "components/Text";
import { TitleText } from "components/Title";
import { useToggleModalWithSound } from "hooks/navigation";
import { paperWhite, white } from "core/styles";
import GameText from "containers/Game/GameText";
import RulesMobile from "pages/RulesMobile";
import { DEFAULT_ROLE_CONFIG } from "core/constants";
import { calculateRoleBreakdown, formatRoleWarning } from "core/utils/roles";

const BatContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 5%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const StickyBottom = styled.div`
  background: inherit;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const QRCodeWrapper = styled.div`
  position: fixed;
  top: 46.5vw;
  left: 59.35vw;
  transform: rotate(5deg);
  width: 10vw;
  height: 10vw;
`;

const HostContainer = styled.div`
  position: fixed;
  top: 44vw;
  right: 50.3vw;
  transform: rotate(1deg);
  width: 10vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  & ${TextStyle} {
    margin-top: 2vw;
    word-break: break-all;
  }
`;

const GuestTitleContainer = styled.div`
  position: fixed;
  top: 34.2vw;
  left: 18.7vw;
  transform: rotate(-11deg);
  width: 10vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const getSizeFromNumberOfPlayers = ({ numberOfPlayers }) => {
  if (numberOfPlayers >= 12) return 1.5;
  if (numberOfPlayers >= 8) return 2;
  return 3;
};

const GuestList = styled.div`
  position: fixed;
  top: 37.2vw;
  left: 17.4vw;
  transform: rotate(-11deg);
  text-align: center;
  width: 17vw;
  & ${TextStyle} {
    word-break: break-all;
    line-height: ${getSizeFromNumberOfPlayers}vw;
    font-size: ${getSizeFromNumberOfPlayers}vw;
  }
  box-sizing: border-box;
`;

const RoleWarningContainer = styled.div`
  margin: 20px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  white-space: pre-line;
  color: ${white};
`;

const WaitingRoom = ({
  playerName,
  gameSessionId,
  vipReady,
  startGame,
  startGameWithIntro,
  settings,
  onSaveSettings,
  isVIP,
  isSettingsLocked,
  onVIPExit,
  gameState,
  error,
  players = [],
  waiting,
  showSpinner,
}) => {
  // Debug logging to trace GameSettings rendering issue
  console.log("🔍 WaitingRoom render:", {
    settings,
    isVIP,
    isSettingsLocked,
    hasSettings: !!settings,
    playerName,
    gameSessionId,
    players,
  });

  const location = useLocation();
  const isDesktopMode = location.pathname.startsWith("/desktop");

  // Setup flavor text based on mode
  const flavorSource = isDesktopMode ? desktop : web;
  const { waitingRoom: waitingRoomFlavor, general, actions } = flavorSource;
  // Use web.actions as fallback since desktop flavor doesn't have actions
  const flavor = {
    ...web.actions,
    ...general,
    ...waitingRoomFlavor,
    ...actions,
  };

  // Setup QR code URL for desktop mode
  const isDev = process.env.NODE_ENV === "development";
  const BASE_URL = isDev ? "localhost:3003" : "www.vampireparty.fun";
  const PROTOCOL = isDev ? "http://" : "https://";
  const fullGameSessionUrl = PROTOCOL + BASE_URL + "/#" + gameSessionId;

  // Setup toggle modal for back buttons
  const toggleModal = useToggleModalWithSound();

  // May never put back
  // const text = vip
  //   ? processText(flavor.vipWelcomeText, { playerName, gameSessionId })
  //   : processText(flavor.welcomeText, { playerName, gameSessionId });

  const [showSettings, setShowSettings] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationSeed, setConfirmationSeed] = useState(null);

  const homeView = !showSettings && !showRules;

  // Calculate player counts before useMemo
  const numberOfPlayers = players.length;
  const minNumberOfPlayers =
    settings?.roleConfig?.minPlayers || DEFAULT_ROLE_CONFIG.minPlayers;

  // Calculate role breakdown for warning display
  const roleBreakdown = useMemo(() => {
    if (!settings?.roleConfig || numberOfPlayers < minNumberOfPlayers) {
      return null;
    }
    return calculateRoleBreakdown(
      settings.roleConfig,
      numberOfPlayers,
      settings?.rolePreviewSeed
    );
  }, [settings, minNumberOfPlayers, numberOfPlayers]);

  console.log({ roleBreakdown });
  // Handlers for confirmation modal
  const handleStartGame = () => {
    if (!settings?.roleConfig) {
      // No role config, start game normally
      startGame();
      return;
    }

    const breakdown = calculateRoleBreakdown(
      settings.roleConfig,
      numberOfPlayers
    );

    // Check if confirmation needed (more roles configured than players - requires randomization)
    const needsConfirmation =
      breakdown && breakdown.totalConfigured > numberOfPlayers;

    if (needsConfirmation) {
      setConfirmationSeed(Math.floor(Math.random() * 1000000));
      setShowConfirmModal(true);
    } else {
      startGame();
    }
  };

  const handleConfirmStart = () => {
    setShowConfirmModal(false);
    // Pass roleSeed to startGame via options parameter
    startGame({ roleSeed: confirmationSeed });
  };

  const handleRandomizeModal = () => {
    setConfirmationSeed(Math.floor(Math.random() * 1000000));
  };

  if (isDesktopMode) {
    return (
      <Wrapper data-test-id="waiting-room-desktop">
        <SelectableLink onClick={toggleModal} backButton />
        <SelectableLink onClick={toggleModal} backButton waitingRoom />

        {!error && gameSessionId && (
          <QRCodeWrapper>
            <QRCode
              value={fullGameSessionUrl}
              bgColor={paperWhite}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </QRCodeWrapper>
        )}

        {gameSessionId && (
          <HostContainer>
            <TitleText>{processText(flavor.code)}</TitleText>
            <TextStyle>{gameSessionId}</TextStyle>
          </HostContainer>
        )}

        <GuestTitleContainer>
          <TitleText>{processText(flavor.guests)}</TitleText>
        </GuestTitleContainer>

        <GuestList numberOfPlayers={players.length} data-test-id="guest-list">
          {!error &&
            players.map(({ userName }) => (
              <TextStyle key={userName}>{userName}</TextStyle>
            ))}
        </GuestList>

        <StickyBottom>
          <ButtonContainer>
            {vipReady && (
              <Button data-test-id="start-game-button" onClick={startGame}>
                {processText(flavor.startGame)}
              </Button>
            )}
          </ButtonContainer>
        </StickyBottom>
      </Wrapper>
    );
  }

  console.log("🔍 WaitingRoom render (non-desktop):", { players });

  return (
    <Wrapper data-test-id="waiting-room">
      {!isVIP && (
        <BatContainer>
          <BatFlapping />
        </BatContainer>
      )}

      {showSettings && settings && (
        <GameSettings
          settings={settings}
          onSave={onSaveSettings}
          isVIP={isVIP}
          isLocked={isSettingsLocked}
          onClickBack={() => setShowSettings(false)}
        />
      )}
      {showRules && <RulesMobile onClickBack={() => setShowRules(false)} />}
      {homeView && (
        <>
          <GameText title={"Room"} data-test-id="guest-list">
            {gameSessionId}
            {numberOfPlayers < minNumberOfPlayers &&
              `Waiting for ${
                minNumberOfPlayers - numberOfPlayers
              } more players to join`}
            {vipReady && `Waitin for your host to start the game`}
          </GameText>
          <GameText title={"Guests"} data-test-id="guest-list" isLong>
            {players
              .filter(({ id }, index, array) => {
                return array.findIndex((item) => item.id === id) === index;
              })
              .map(({ userName, vip }) => (
                <>
                  <TextStyle key={userName}>
                    {userName}
                    {vip ? " (Host)" : ""}
                  </TextStyle>
                </>
              ))}
          </GameText>
          {roleBreakdown && roleBreakdown.needsRandomization && (
            <RoleWarningContainer data-test-id="role-warning">
              {formatRoleWarning(roleBreakdown)}
            </RoleWarningContainer>
          )}
          <StickyBottom>
            <ButtonContainer>
              {isVIP && numberOfPlayers >= minNumberOfPlayers && (
                <Button
                  data-test-id="preview-roles-button"
                  onClick={() => {
                    // Generate a new seed that produces a different role distribution
                    const currentBreakdown = roleBreakdown;
                    let newSeed;
                    let newBreakdown;
                    let attempts = 0;
                    const maxAttempts = 100; // Safety limit

                    do {
                      newSeed = Math.floor(Math.random() * 1000000);
                      newBreakdown = calculateRoleBreakdown(
                        settings.roleConfig,
                        numberOfPlayers,
                        newSeed
                      );
                      attempts++;
                    } while (
                      attempts < maxAttempts &&
                      JSON.stringify(newBreakdown?.roles) ===
                        JSON.stringify(currentBreakdown?.roles)
                    );

                    onSaveSettings({
                      ...settings,
                      rolePreviewSeed: newSeed,
                    });
                  }}
                >
                  Randomize Roles
                </Button>
              )}
              {vipReady && (
                <Button
                  data-test-id="start-game-button"
                  onClick={handleStartGame}
                >
                  {processText(flavor.startGame)}
                </Button>
              )}
              {!showRules && (
                <Button
                  data-test-id="game-rules-button"
                  onClick={() => {
                    setShowRules(true);
                  }}
                >
                  Rules
                </Button>
              )}
              {!showSettings && (
                <Button
                  data-test-id="game-settings-button"
                  onClick={() => {
                    setShowSettings(true);
                  }}
                >
                  Game Settings
                </Button>
              )}
              {isVIP && onVIPExit && (
                <VIPExitButton
                  onConfirmExit={onVIPExit}
                  gameState={gameState}
                />
              )}
            </ButtonContainer>
          </StickyBottom>
        </>
      )}
      {showConfirmModal && settings?.roleConfig && (
        <RoleConfirmationModal
          breakdown={calculateRoleBreakdown(
            settings.roleConfig,
            numberOfPlayers,
            confirmationSeed
          )}
          currentPlayerCount={numberOfPlayers}
          onConfirm={handleConfirmStart}
          onCancel={() => setShowConfirmModal(false)}
          onRandomize={handleRandomizeModal}
        />
      )}
    </Wrapper>
  );
};

export default WaitingRoom;
